// src/views/MenageCalendrierView.jsx — onglet « Mois ».
// Refonte du 2026-08-11, maquette validée Dimitri.
//
// ⚠️ SEUL CE QUI APPELLE UNE ACTION SE COLORE. La règle tient en une phrase, et
// elle est écrite sous la grille : une case colorée demande quelque chose, le
// reste s'est bien passé. Le validé et le « à venir » perdent donc tout fond —
// cinq fonds pâles tous à 96 % de luminosité se confondaient à distance, et
// coloraient surtout ce sur quoi il n'y a rien à faire.
//
// ⚠️ PLUS JAMAIS DE NOMBRE NU. Le chiffre changeait de sens avec la couleur
// (corail = total du jour, rouge = non-validés) : deux cases marquées « 2 » ne
// parlaient pas de la même chose. Chaque case écrit désormais son libellé en
// toutes lettres — « 2 à valider », « 4 prévus », « 2 sans agent ».
//
// ⚠️ ET PLUS RIEN EN INFOBULLE. Cette page s'utilise au doigt : ce qui
// n'apparaît qu'au survol n'existe pas sur mobile. Tout ce que portait un
// `title=` est monté dans la case.
//
// ⚠️ LES INTERVENTIONS COMPTENT. La grille ne comptait que des ménages : une
// intervention posée dans la frise n'existait pas dans le mois, alors qu'elle
// occupe l'agent autant qu'un ménage.
//
// Vue partagée 'menage' / 'orchestrateur' — même écran, même code : seuls le
// TITRE et le PÉRIMÈTRE changent. Source : menage_runs RLS-scopé (agent = ses
// runs ; orchestrateur = pool ∧ logIds + orphelins). AUCUN montant, AUCUN nom de
// voyageur ne remonte jusqu'à la grille.

import { useState, useEffect, useMemo, useCallback } from "react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import CarteTitrePage from "../components/CarteTitrePage"
import { buildMonthGrid } from "../components/calendar/_grid-geometry"
// ⚠️ `todayParisIso` VIENT D'ICI, ET DE NULLE PART AILLEURS. Cette vue en avait
// sa PROPRE copie, identique à la ligne près. Deux sources de « date du jour »
// dans la même application finissent toujours par diverger — ici au passage de
// minuit : la case du 11 août est restée « aujourd'hui » pour un écran et est
// devenue « en retard » pour l'autre, le même jour, à la même seconde.
import {
  computeVisibleEmails, computeAssignOptions, buildRunsProviderFilter, todayParisIso,
} from "./MenageTodayView"
import { libelleDepuisEmail, initialsFromEmail } from "./menage/RunCard"
import MoisJourDetail from "./menage/MoisJourDetail"
import { resolveLogementNameFromCfg } from "./menage/MenageDayDetail"
import { ModaleDuree } from "./menage/FriseJournee"
import BasculeEspace from "../components/BasculeEspace"
import { chargerControlesParRun } from "../lib/controles-suivi-api"
import { chargerControleurDefauts } from "../lib/panneau-menage-api"
import PanneauDetailControle from "../components/PanneauDetailControle"
import { Eye, ChevronDown } from "lucide-react"
import { User as UserIconBascule } from "lucide-react"
import ChecklistRunModal from "../modals/ChecklistRunModal"
import IncidentModal from "../modals/IncidentModal"
import { STATUS, createIncident } from "../lib/incidents-api"
import { chargerRegles } from "../lib/menage-regles-api"
import { fixerDureeRun } from "../lib/menage-frise-api"
import { fixerDureeDefautLogement } from "../lib/menage-duree-api"
import { fetchLogInfo } from "../lib/menage-log-info-cache"
import { versLaFrise, versLaDemande, pourOnglet, ONGLET_MOIS } from "../lib/orchestrateur-navigation"
import { chargerInterventions } from "../lib/menage-frise-api"
import { dureeDuMenage , formaterDuree} from "../lib/menage-frise"
import {
  etatDuJour, pastilleDuJour, agregerMois, chargeParAgent, chargeParLogement,
  jourEtMois,
  titreMois,
} from "../lib/menage-mois"
import { ChevronLeft, ChevronRight, AlertTriangle, Check, UserRoundSearch, Clock, Home, ClipboardX, X, CalendarDays } from "lucide-react"


// ── PALETTE — charte 2026. AUCUNE autre couleur. ─────────────────────────────
const C = {
  gris: '#3d5166', fonce: '#2e3d4d', ciel: '#87b8e6', cielPale: '#e4f0fb',
  colonne: '#f4f9fd', bord: '#d5e3ee', muet: '#98a2ad', texteDoux: '#5c6773',
  // Le seul fond d'alerte de la grille, et le liseré qui distingue le retard
  // (on peut encore agir) du sans-agent (on doit encore décider).
  alerteFond: '#f7dcd1', alerteBord: '#b8412b', alerteTexte: '#8f2f1d',
  accent: '#c1512d',
  bandeauAlerte: '#f5a892',
  // La pastille « demande d'horaire » : le ciel APPUYÉ du dégradé de marque —
  // ni le ciel signature (réservé à la sélection), ni le navy (invisible sur
  // les cases terracotta, constat Dimitri 2026-08-14).
  demandeFond: '#5c8ab8',
  // Le validé et le « à venir » n'ont pas de fond : juste un contour très pâle.
  contour: '#eef1f4', valideTexte: '#b8c4cd', aVenirNum: '#5c6773',
  horsMois: '#dde2e7', semaine: '#e8f2fa',
  vertFond: '#f0f7f3',
  // ⚠️ LES DEUX FONDS DE PASTILLE CALMES, FONCÉS D'UN CRAN (Dimitri 2026-08-12).
  // À #e8f2fa et #f0f7f3 le rond disparaissait sur le blanc de la case : il ne
  // restait que deux chiffres nus côte à côte — exactement ce que la pastille
  // existait pour empêcher. Ce sont des constantes SÉPARÉES de `semaine` et de
  // `vertFond`, qui gardent leur teinte : la carte « semaine la plus chargée »
  // et l'en-tête d'un ménage validé sont grands, eux, et n'ont pas ce problème.
  // ⚠️ NI CONTOUR NI FOND PLEIN : à 20 px un trait d'1 px salit le bord sans se
  // lire, et une pastille pleine ferait du VALIDÉ l'élément le plus visible de
  // la grille — alors qu'il n'y a rien à y faire.
  pastAVenir: '#d4e6f5', pastAVenirTexte: '#22303d',
  pastValide: '#dbe9e2', pastValideTexte: '#1f5a43',
  inter: '#7b6bb0',
}

// 186 → 262 (correction Dimitri 2026-08-22) : la colonne portait le détail
// « ligne par ligne » du récapitulatif en tronquant tous les noms — illisible,
// alors que la lisibilité est sa raison d'être. La grille reste bornée à
// 720 px et centrée : elle ne perd rien.
const LARGEUR_COLONNE = 262

// Media query : la colonne passe SOUS la grille sur mobile, même règle que
// Suivi équipe. `position:sticky` exige `align-self:flex-start` — une colonne
// étirée n'a nulle part où coller.
const CSS_MOIS = `
.fbz-mois{display:grid;grid-template-columns:minmax(0,1fr) ${LARGEUR_COLONNE}px;gap:0;align-items:start}
.fbz-mois-aside{border-left:1px solid ${C.bord};background:${C.colonne};padding:18px 14px 30px;position:sticky;top:0;align-self:flex-start}
.fbz-mois-main{padding:16px 18px 60px;min-width:0}
/* ⚠️ LA GRILLE EST BORNÉE, PAS LA PAGE. Les cases sont CARRÉES : élargies à la
   pleine largeur du conteneur, elles passeraient de 85 à 150 px de côté et la
   grille gagnerait près de 400 px de HAUTEUR — on aurait échangé un débordement
   à droite contre un débordement en bas, en rendant chaque case démesurée pour
   ce qu'elle porte (un numéro et une pastille).
   ⚠️ ET CENTRÉE. Bornée mais collée à gauche, elle laissait 176 px de vide entre
   elle et la colonne de contexte — un blanc qui se lit comme un morceau
   manquant, pas comme une marge.
   ⚠️ 720 px EST UN ARBITRAGE TRANCHÉ (Dimitri, 2026-08-11), pas une valeur par
   défaut. À 560 px la page tiendrait dans 900 px de haut sans défilement, mais
   les cases tomberaient à 76 px de côté : trop petit à toucher. On garde des
   cases confortables et on accepte ~190 px de défilement vertical. Ne pas
   rétrécir la grille pour gagner du scroll — la question a été posée et
   tranchée dans ce sens. */
.fbz-mois-colonne{max-width:720px;margin:0 auto}
@media (max-width: 860px){
  .fbz-mois{grid-template-columns:minmax(0,1fr)}
  .fbz-mois-aside{border-left:none;border-top:1px solid ${C.bord};position:static}
  .fbz-mois-main{padding:14px 12px 24px}
}
`

function parseMonth(monthStr) {
  const [y, m] = String(monthStr).split('-').map(Number)
  return { y, m }
}
function formatMonth({ y, m }) {
  return `${y}-${String(m).padStart(2, '0')}`
}
function shiftMonth(monthStr, delta) {
  const { y, m } = parseMonth(monthStr)
  const d = new Date(y, m - 1 + delta, 1)
  return formatMonth({ y: d.getFullYear(), m: d.getMonth() + 1 })
}
function monthLabelFR(monthStr) {
  const { y, m } = parseMonth(monthStr)
  return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
}

/** « Juillet » — le nom seul, capitalisé, pour « Juillet est clos ». */
function nomDuMois(monthStr) {
  const { y, m } = parseMonth(monthStr)
  const s = new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long' })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** « lundi 18 » — le jour nommé des cartes d'action. */
function jourNomme(iso) {
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric' })
}

/** « 16, 18 et 21 août » — la sous-ligne d'une carte ACTIVE liste les jours
 *  (tous du même mois par construction : ce sont des jours de la grille). */
function listeNumerosJours(isos) {
  const liste = Array.isArray(isos) ? isos : []
  if (!liste.length) return ''
  const nums = liste.map((iso) => String(Number(iso.slice(-2))))
  const mois = jourEtMois(liste[0]).replace(/^\d+\s*/, '')
  const corps = nums.length > 1 ? `${nums.slice(0, -1).join(', ')} et ${nums[nums.length - 1]}` : nums[0]
  return `${corps} ${mois}`
}

/** UNE CARTE D'ACTION du bandeau (maquette 2026-08-15) : icône, titre, sous-
 *  ligne, chevron — son clic MET AU TRAVAIL (voile + premier jour déplié).
 *  ACTIVE : anneau blanc 2 px, le chevron devient une CROIX, la sous-ligne
 *  liste les jours. Second clic : tout se rallume. */
function CarteActionTitre({ testId, fond, icone, titre, titreCouleur, sousLigne,
  sousLigneCouleur, chevronCouleur, onClic, active = false }) {
  return (
    <button type="button" data-testid={testId} onClick={onClic}
      className="fbz-press fbz-cible-tactile"
      aria-pressed={active}
      style={{ flex: 1, minWidth: 200, display: 'flex', alignItems: 'center', gap: 10,
        background: fond, border: 'none', borderRadius: 9, padding: '9px 12px',
        cursor: 'pointer', textAlign: 'left', fontFamily: DS.fontBody,
        boxShadow: active ? '0 0 0 2px #fff' : 'none' }}>
      {icone}
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: titreCouleur }}>
          {titre}
        </span>
        <span style={{ display: 'block', fontSize: 10.5, marginTop: 1, color: sousLigneCouleur,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sousLigne}
        </span>
      </span>
      {active
        ? <X size={15} strokeWidth={2.4} color={chevronCouleur} style={{ flexShrink: 0 }} />
        : <ChevronRight size={15} strokeWidth={2.4} color={chevronCouleur} style={{ flexShrink: 0 }} />}
    </button>
  )
}

// ── L'APPARENCE D'UNE CASE ──────────────────────────────────────────────────
//
// ⚠️ DEUX COULEURS, PAS CINQ. Une case colorée demande quelque chose ; tout le
// reste s'est bien passé et n'a donc pas à attirer l'œil.
const STYLE_CASE = {
  retard:     { fond: C.alerteFond, ombre: `inset 0 0 0 2px ${C.alerteBord}`, num: C.alerteTexte,
                pastFond: C.alerteTexte, pastTexte: '#fff' },
  sans_agent: { fond: C.alerteFond, ombre: 'none', num: C.alerteTexte,
                pastFond: '#fff', pastTexte: C.alerteTexte },
  // ⚠️ AUJOURD'HUI N'EST PLUS UN APLAT (Dimitri 2026-08-14) : la règle écrite
  // sous la grille dit « une case colorée demande quelque chose » — et
  // aujourd'hui ne demande rien. Une BORDURE situe le jour sans le faire crier
  // plus fort que les vraies alertes ; la pastille navy pleine garde le compte
  // lisible.
  aujourdhui: { fond: 'transparent', ombre: `inset 0 0 0 2px ${C.gris}`, num: C.fonce,
                pastFond: C.gris, pastTexte: '#fff' },
  valide:     { fond: 'transparent', ombre: `inset 0 0 0 1px ${C.contour}`, num: C.muet,
                pastFond: C.pastValide, pastTexte: C.pastValideTexte },
  a_venir:    { fond: 'transparent', ombre: `inset 0 0 0 1px ${C.contour}`, num: C.aVenirNum,
                pastFond: C.pastAVenir, pastTexte: C.pastAVenirTexte },
  vide:       { fond: 'transparent', ombre: `inset 0 0 0 1px ${C.contour}`, num: C.aVenirNum,
                pastFond: 'transparent', pastTexte: C.muet },
  // GO Dimitri 2026-08-19 — les CONTRÔLES entrent dans la grille :
  // « repérer d'un coup d'œil les jours qui ont posé problème ».
  controle_reprendre: { fond: '#fdf4f0', ombre: 'inset 0 0 0 1px #e0b5a5', num: '#8f2f1d',
                pastFond: '#fff', pastTexte: '#8f2f1d' },
  valide_total: { fond: '#f7fbf9', ombre: 'inset 0 0 0 1px #c9e0d4', num: C.muet,
                pastFond: '#7fc4a3', pastTexte: '#fff' },
}

export default function MenageCalendrierView({ basculePage = null, cfg, currentUser, destination, onAller, onDestinationConsommee, onControler = null }) {
  const today = useMemo(() => todayParisIso(), [])
  const [month, setMonth] = useState(today.slice(0, 7))
  const [runs, setRuns] = useState([])
  const [interventions, setInterventions] = useState([])
  const [horaires, setHoraires] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // ── LES CONTRÔLES DU MOIS (GO Dimitri 2026-08-19) — UN SEUL appel groupé
  // (.in sur les runs du mois), déclenché par le RÉSULTAT des runs, jamais au
  // chargement. BEST-EFFORT : un échec rend {} — pas de signes, jamais un
  // écran cassé. RLS : admin tout, orchestrateur pool ; l'AGENT ne lit pas
  // les contrôles (anonymat) → on ne lance même pas la requête.
  const [controlesParRun, setControlesParRun] = useState({})
  // LE CONTRÔLEUR DÉSIGNÉ par logement — l'état « personne de désigné » du
  // rond de contrôle des lignes du jour (point 5, Dimitri 2026-08-22).
  const [controleursDefaut, setControleursDefaut] = useState({})
  // Le détail LECTURE SEULE d'un contrôle fait — même panneau que la frise.
  const [detailControle, setDetailControle] = useState(null)
  useEffect(() => {
    const role = currentUser?.role
    if (role !== 'admin' && role !== 'orchestrateur') { setControlesParRun({}); return }
    const ids = [...new Set(runs.map((r) => r?.id).filter(Boolean))]
    if (!ids.length) { setControlesParRun({}); return }
    let annule = false
    ;(async () => {
      const par = await chargerControlesParRun(supabase, ids)
      if (!annule) setControlesParRun(par)
      // Le contrôleur désigné par logement — la même fenêtre de runs donne
      // la liste des logements concernés, un seul aller-retour.
      const logIds = [...new Set(runs.map((r) => r.logement_id).filter(Boolean))]
      const defauts = await chargerControleurDefauts(supabase, logIds)
      if (!annule) setControleursDefaut(defauts)
    })()
    return () => { annule = true }
  }, [runs, currentUser?.role])

  // Par JOUR : combien de contrôles ATTENDENT une reprise.
  const controlesParJour = useMemo(() => {
    const out = {}
    for (const r of runs) {
      const c = controlesParRun[r.id]
      if (!c) continue
      const attend = c.statut === 'a_reprendre' || c.statut === 'reprise_en_cours'
      if (!out[r.scheduled_date]) out[r.scheduled_date] = { aReprendre: 0 }
      if (attend) out[r.scheduled_date].aReprendre += 1
    }
    return out
  }, [runs, controlesParRun])

  // Le clic-jour DÉPLIE le détail sous la grille (re-clic = replie).
  const [selectedDayIso, setSelectedDayIso] = useState(null)
  // ⚠️ LE RATTRAPAGE OUVRE LES JOURS EN RETARD D'UN COUP. Il fallait balayer
  // trente cases pour trouver les rouges, puis les ouvrir une par une.
  // ⚠️ LE FILTRE DES CARTES D'ACTION (maquette 2026-08-15) : le clic MET AU
  // TRAVAIL — voile sur la grille + premier jour concerné déplié + « jour
  // suivant ». { type: 'sans_agent' | 'checklists', jour: iso }. Les deux
  // filtres sont EXCLUSIFS ; cliquer une case pendant qu'un filtre est actif
  // ouvre ce jour-là SANS casser le filtre.
  const [filtreAction, setFiltreAction] = useState(null)

  const handleDayClick = useCallback((iso) => {
    // ⚠️ le filtre d'une carte d'action SURVIT au clic sur une case : on ouvre
    // ce jour-là, le voile reste (consigne explicite 2026-08-15).
    setSelectedDayIso(prev => (prev === iso ? null : iso))
  }, [])

  const isOrchestrateur = currentUser?.role === 'orchestrateur'
  const visibleEmails = useMemo(() => computeVisibleEmails(currentUser), [currentUser])

  const bornes = useMemo(() => {
    const { y, m } = parseMonth(month)
    const lastDay = new Date(y, m, 0).getDate()
    return {
      debut: `${y}-${String(m).padStart(2, '0')}-01`,
      fin: `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    }
  }, [month])

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      // ⚠️ PÉRIMÈTRE MINIMAL CONSERVÉ. On ajoute EXACTEMENT ce que les nouveaux
      // libellés exigent : l'agent (« sans agent », charge par agent) et la
      // durée d'exception (les heures du mois). Ni montant, ni voyageur, ni
      // photo ne remontent jusqu'à la grille — la frontière anti-PII tient.
      // `modifie_par`/`modifie_le` : la trace « dernière main » de la frise,
      // affichée dans la carte du dépliage (ajout 2026-08-14). Aucune PII
      // au-delà de l'email d'équipe déjà présent sur la ligne.
      const { data, error: rErr } = await buildRunsProviderFilter(
        supabase.from('menage_runs')
          // + completed_at (« validé à 13h ») et guest_count (carte ARRIVÉE) — recalage détail 2026-08-19.
          // + heure_fixee : l'heure des ménages À VENIR du détail par agent (2026-08-22).
          .select('id, logement_id, scheduled_date, status, assigned_provider_email, duree_min, modifie_par, modifie_le, completed_at, heure_fixee, guest_count'),
        currentUser,
        visibleEmails
      ).gte('scheduled_date', bornes.debut).lte('scheduled_date', bornes.fin)
      if (rErr) throw rErr
      setRuns(data || [])
    } catch (e) { setError(e.message || String(e)) }
    finally { setLoading(false) }
  }, [bornes, currentUser, visibleEmails])
  useEffect(() => { reload() }, [reload])

  // INTERVENTIONS du mois — la RLS borne le périmètre, comme pour les ménages.
  // ⚠️ POUR TOUS LES RÔLES, PAS SEULEMENT L'ORCHESTRATEUR (bug corrigé
  // 2026-08-14) : gaté `isOrchestrateur`, le mois de l'AGENT ignorait ses
  // propres interventions — sa grille ne les comptait pas alors que son écran
  // « Aujourd'hui » les mêle aux ménages. La policy `interventions_menage_select`
  // lui rend exactement LES SIENNES : ouvrir le gate suffit, la RLS fait foi.
  const rechargerInterventions = useCallback(async () => {
    try {
      const { interventions: list } = await chargerInterventions(supabase, { du: bornes.debut, au: bornes.fin })
      setInterventions(Array.isArray(list) ? list : [])
    } catch (e) {
      console.error('[mois] interventions', e?.message || e)
    }
  }, [bornes])
  useEffect(() => { rechargerInterventions() }, [rechargerInterventions])

  // DEMANDES D'HORAIRES en attente — une demande porte une DATE, et l'écran qui
  // décide dessus existe : les deux doivent se parler.
  useEffect(() => {
    if (!isOrchestrateur) { setHoraires([]); return }
    let annule = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'services_orchestrateur_hourly_list' }),
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (!annule) setHoraires(Array.isArray(json?.pending) ? json.pending : [])
      } catch (e) {
        // Jamais bloquant : sans demandes, le point ne s'affiche pas, l'écran vit.
        console.error('[mois] horaires', e?.message || e)
      }
    })()
    return () => { annule = true }
  }, [isOrchestrateur, currentUser?.email])

  // ── CE QUE LE DÉPLIAGE A BESOIN DE SAVOIR ─────────────────────────────────
  // Horaires, adresse, code : servis par le GUICHET cloisonné (`menage_log_info`),
  // jamais par cfg — la RLS de `public.storage` refuse `fbz8-cfg` aux rôles
  // terrain. Chargé pour les SEULS jours dépliés : un mois entier ferait
  // trente requêtes pour des informations qu'on ne regarde pas.
  const [logInfos, setLogInfos] = useState({})
  const [regles, setRegles] = useState({})
  const [guestTimes, setGuestTimes] = useState({})
  const [checklistModal, setChecklistModal] = useState(null)
  const [incidentModal, setIncidentModal] = useState(null)
  const [modaleDuree, setModaleDuree] = useState(null)
  const [message, setMessage] = useState(null)

  const assignOptions = useMemo(() => computeAssignOptions(currentUser), [currentUser])

  useEffect(() => {
    if (!isOrchestrateur) { setRegles({}); return }
    let annule = false
    ;(async () => {
      try {
        const { regles: r } = await chargerRegles(supabase)
        if (!annule) setRegles(r || {})
      } catch (e) { console.error('[mois] regles', e?.message || e) }
    })()
    return () => { annule = true }
  }, [isOrchestrateur])

  const infoDe = useCallback((logementId) => logInfos[logementId] || null, [logInfos])
  const nomDe = useCallback(
    (logementId) => logInfos[logementId]?.nom || resolveLogementNameFromCfg(cfg, logementId) || logementId,
    [logInfos, cfg]
  )

  // Les jours DÉPLIÉS seulement (voir plus haut) — recalculé après `joursDeplies`.
  const chargerInfos = useCallback(async (ids) => {
    const manquants = ids.filter((id) => id && !logInfos[id])
    if (!manquants.length) return
    const entrees = await Promise.all(manquants.map(async (lid) => {
      try {
        const info = await fetchLogInfo(lid, null)
        const adresse = [info?.adresse_complete,
          [info?.code_postal, info?.ville].filter(Boolean).join(' ')]
          .filter((x) => x && String(x).trim()).join(' · ')
        return [lid, {
          nom: info?.nom || null,
          checkout: info?.timing?.checkout_time || null,
          checkin: info?.timing?.checkin_time || null,
          adresse: adresse || null,
          boiteCles: (info?.codes_acces?.boite_cles && String(info.codes_acces.boite_cles).trim()) || null,
          dureeMenage: info?.menage?.menage_duree_min ?? null,
        }]
      } catch (e) {
        console.error('[mois] menage_log_info', lid, e?.message || e)
        return null
      }
    }))
    const utiles = entrees.filter(Boolean)
    if (utiles.length) setLogInfos((p) => ({ ...p, ...Object.fromEntries(utiles) }))
  }, [logInfos])

  // ⚠️ RÉASSIGNATION : UPDATE mono-colonne + ROWCOUNT-CHECK. Un refus RLS ne
  // lève PAS d'erreur — il renvoie zéro ligne. Sans ce contrôle, l'écran
  // annoncerait « réassigné » sur une écriture que le serveur a refusée.
  const handleReassign = useCallback(async (run, email) => {
    setMessage(null)
    if (!email || email === run.assigned_provider_email) return
    try {
      const { data, error: e } = await supabase.from('menage_runs')
        .update({ assigned_provider_email: email }).eq('id', run.id).select('id')
      if (e || !data || data.length === 0) {
        setMessage('❌ Réassignation refusée (hors pool / hors périmètre / run verrouillé ou terminé).')
        return
      }
      setMessage('✅ Ménage réassigné.')
      await reload()
    } catch (e) {
      console.error('[mois] reassign', e?.message || e)
      setMessage('❌ Réassignation refusée (hors pool / hors périmètre / run verrouillé ou terminé).')
    }
  }, [reload])

  // ── RÉCAPITULATIF (recadrage Dimitri 2026-08-22) : AUCUN FILTRE — les deux
  // modes montrent tout le mois affiché. L'ancien filtre par ligne (14/08) est
  // retiré ; toucher un agent DÉPLIE son détail (liste de ses ménages), et
  // « Par logement » ne se déplie pas — pas le même usage.
  const [modeRecap, setModeRecap] = useState('agent') // 'agent' | 'logement'
  const [chargeDepliee, setChargeDepliee] = useState(null) // email | logement_id | null
  const runsAffiches = runs
  const interventionsAffichees = interventions

  // ── AGRÉGATION ────────────────────────────────────────────────────────────
  const mois = useMemo(
    () => agregerMois({ runs: runsAffiches, interventions: interventionsAffichees, todayIso: today }),
    [runsAffiches, interventionsAffichees, today]
  )

  const joursAvecDemande = useMemo(() => {
    const s = new Set()
    for (const d of horaires) {
      if (d?.event_date) s.add(d.event_date)
    }
    return s
  }, [horaires])

  // La durée d'un ménage vient de la RÈGLE UNIQUE : exception du run, puis
  // défaut du logement (cfg), puis 90. Jamais un second calcul.
  const logementDe = useCallback((logementId) => {
    for (const prop of cfg?.props || []) {
      for (const log of prop.logs || []) {
        if (log.id === logementId) return log
        for (const ch of log.chambres || []) if (ch.id === logementId) return ch
      }
    }
    return null
  }, [cfg])
  const dureeDe = useCallback(
    (run) => dureeDuMenage(run, logementDe(run?.logement_id)),
    [logementDe]
  )

  const charges = useMemo(
    () => chargeParAgent({ runs: runsAffiches, interventions: interventionsAffichees, dureeDe }),
    [runsAffiches, interventionsAffichees, dureeDe]
  )
  const chargesLogement = useMemo(
    () => chargeParLogement({ runs: runsAffiches, interventions: interventionsAffichees, dureeDe }),
    [runsAffiches, interventionsAffichees, dureeDe]
  )
  // Les NOMS du récap par logement viennent du guichet cloisonné — chargés à
  // l'ouverture du mode seulement (même règle « à la demande » que le dépliage).
  useEffect(() => {
    if (modeRecap !== 'logement') return
    const ids = chargesLogement.map((c) => c.logementId).filter(Boolean)
    if (ids.length) chargerInfos(ids)
  }, [modeRecap, chargesLogement, chargerInfos])
  // ⚠️ ON COMPTE DES CHECKLISTS, PAS DES JOURS (correction Dimitri 2026-08-15) :
  // deux ménages non validés le même jour font DEUX checklists. Et « pas
  // validées », pas « à rattraper » — il ne faut pas refaire le ménage, juste
  // renseigner ce qui s'est passé.
  // ⚠️ LE JOUR COURANT COMPTE (correction Dimitri 2026-08-22) : la case
  // d'aujourd'hui garde sa bordure (etatDuJour), mais le BANDEAU, lui, doit
  // dire la vérité — un ménage sans agent AUJOURD'HUI est un manque à nommer.
  const joursSansAgentTous = useMemo(() => {
    const base = mois.joursSansAgent
    const auj = mois.parJour[today]?.sansAgent > 0
    return auj ? [today, ...base.filter((j) => j !== today)] : base
  }, [mois.joursSansAgent, mois.parJour, today])

  const checklistsNonValidees = useMemo(
    () => mois.joursEnRetard.reduce((s, iso) => s + (mois.parJour[iso]?.aValider || 0), 0),
    [mois.joursEnRetard, mois.parJour]
  )

  // ── LA CASE ───────────────────────────────────────────────────────────────
  const renderDaySlot = useCallback((dateIso) => {
    const j = mois.parJour[dateIso]
    const etat = etatDuJour({ dateIso, todayIso: today, ...(j || {}) })
    // LES CONTRÔLES (2026-08-19) : un contrôle à reprendre TEINTE la case
    // (sauf le rouge « retard », plus grave, qui garde son fond — l'œil
    // s'ajoute) ; une journée validée SANS reprise en attente passe au vert
    // plein. Aujourd'hui garde sa bordure navy 2 px quoi qu'il arrive.
    const ctrl = controlesParJour[dateIso] || null
    const aReprendre = ctrl?.aReprendre || 0
    let cleEffective = etat.cle
    if (aReprendre > 0 && etat.cle !== 'retard') cleEffective = 'controle_reprendre'
    else if (etat.cle === 'valide' && !aReprendre) cleEffective = 'valide_total'
    const st = { ...(STYLE_CASE[cleEffective] || STYLE_CASE.vide) }
    if (dateIso === today && cleEffective !== 'aujourdhui') {
      st.ombre = `${!st.ombre || st.ombre === 'none' ? '' : `${st.ombre}, `}inset 0 0 0 2px ${C.gris}`
    }
    const past = pastilleDuJour(etat)
    const aDemande = joursAvecDemande.has(dateIso)
    // ⚠️ GARANTIE TRANSFORMÉE (Dimitri 2026-08-19) : un jour VIDE se rend
    // AUSSI — « un jour vide est une information : il n'y a pas de ménage ».
    // Avant : return null → ni case ni numéro, le mois semblait troué.
    return (
      <div data-testid={`jour-${dateIso}`} data-etat={cleEffective}
        // ⚠️ LE LIBELLÉ COMPLET SURVIT ICI. L'œil lit la forme (un chiffre dans
        // un rond n'est jamais une date) ; une synthèse vocale, elle, a besoin
        // des mots — « 2 à valider », pas « 2 ».
        aria-label={`${Number(String(dateIso).slice(-2))} — ${etat.libelle || 'rien de prévu'}`}
        style={{ position: 'relative', width: '100%', height: '100%', borderRadius: 7,
          padding: 5, background: st.fond, boxShadow: st.ombre, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column' }}>
        {/* ⚠️ PASTILLE HORLOGE, PLUS UN POINT (Dimitri 2026-08-14) : le point
            navy de 6px se noyait sur les cases terracotta et se confondait avec
            la bordure d'« aujourd'hui ». La même horloge que l'onglet Demandes —
            on comprend sans légende (mais elle y entre quand même). */}
        {aDemande && (
          <span data-testid={`jour-demande-${dateIso}`} aria-hidden="true"
            style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20,
              borderRadius: '50%', background: C.demandeFond, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={13} strokeWidth={2.4} color="#fff" />
          </span>
        )}
        <span aria-hidden="true" style={{ fontSize: 11, fontWeight: 700, color: st.num,
          fontFamily: DS.fontBody, lineHeight: 1 }}>
          {Number(String(dateIso).slice(-2))}
        </span>
        {/* ⚠️ LA PASTILLE — c'est la FORME qui la sépare de la date, pas la
            couleur : ça marche aussi pour qui distingue mal les couleurs. */}
        {(past || aReprendre > 0) && (
          <span style={{ marginTop: 'auto', alignSelf: 'flex-start', display: 'inline-flex',
            alignItems: 'center', gap: 4 }}>
            {past && (
              <span data-testid={`jour-pastille-${dateIso}`} aria-hidden="true"
                style={{ display: 'inline-flex',
                  alignItems: 'center', gap: 3, borderRadius: 20, padding: '2px 7px',
                  background: st.pastFond, color: st.pastTexte, fontSize: 9.5, fontWeight: 700,
                  lineHeight: 1.2, fontFamily: DS.fontBody }}>
                {past.icone === 'check' && <Check size={9} strokeWidth={3} color={st.pastTexte} />}
                {past.icone === 'sans-agent' && <UserRoundSearch size={9} strokeWidth={2.6} />}
                {past.nombre}
              </span>
            )}
            {/* L'ŒIL — un contrôle attend une reprise ce jour-là. */}
            {aReprendre > 0 && (
              <span data-testid={`jour-controle-${dateIso}`} aria-label={`${aReprendre} contrôle${aReprendre > 1 ? 's' : ''} à reprendre`}
                style={{ width: 17, height: 17, borderRadius: 9, background: '#b8412b',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <Eye size={10} strokeWidth={2.6} color="#fff" />
              </span>
            )}
          </span>
        )}
      </div>
    )
  }, [mois.parJour, today, joursAvecDemande, controlesParJour])

  const { y: gridYear, m: gridMonth1 } = parseMonth(month)
  // Changer de mois ÉTEINT le filtre : ses jours n'existent plus dans la grille.
  useEffect(() => { setFiltreAction(null) }, [month])

  // Les jours CONCERNÉS par le filtre actif — LE SEUL état de voile de la page
  // (bandeau ET colonne, exclusifs par construction). Les types du bandeau
  // (sans_agent, checklists) OUVRENT en plus le premier jour ; ceux de la
  // colonne (valides, a_venir) éclairent seulement.
  const joursConcernes = useMemo(() => {
    if (!filtreAction) return null
    switch (filtreAction.type) {
      case 'sans_agent': return joursSansAgentTous
      case 'checklists': return mois.joursEnRetard
      case 'valides':
        return Object.keys(mois.parJour).sort()
          .filter((iso) => mois.parJour[iso].total > 0 && mois.parJour[iso].aValider === 0)
      case 'a_venir':
        return Object.keys(mois.parJour).sort()
          .filter((iso) => (iso > today && mois.parJour[iso].total > 0)
            || (iso === today && mois.parJour[iso].aValider > 0))
      default: return null
    }
  }, [filtreAction, mois.joursSansAgent, mois.joursEnRetard, mois.parJour, today])

  // ⚠️ PLUS QUE DEUX SOURCES DE DÉPLIAGE (retrait bande de rattrapage,
  // GO Dimitri 2026-08-15) : le filtre d'une carte d'action (UN jour, celui du
  // filtre ou la case cliquée entre-temps) ou une case sélectionnée à la main.
  // Le « tout déplier d'un coup » est parti avec la bande — porte de sortie
  // notée en roadmap (lien « Tout déplier » dans le pied si ça manque).
  const joursDeplies = useMemo(
    () => (filtreAction?.jour
      ? [selectedDayIso || filtreAction.jour]
      : (selectedDayIso ? [selectedDayIso] : [])),
    [filtreAction, selectedDayIso]
  )

  const runsDeplies = useMemo(
    () => runsAffiches.filter((r) => joursDeplies.includes(r.scheduled_date)),
    [runsAffiches, joursDeplies]
  )

  useEffect(() => {
    const ids = [...new Set(runsDeplies.map((r) => r.logement_id).filter(Boolean))]
    if (ids.length) chargerInfos(ids)
  }, [runsDeplies, chargerInfos])

  // Heures RÉELLES du voyageur — ORCHESTRATEUR uniquement (ADR-226), et
  // seulement pour les jours ouverts.
  useEffect(() => {
    if (!isOrchestrateur) { setGuestTimes({}); return }
    const ids = runsDeplies.map((r) => r.id).filter(Boolean)
    if (!ids.length) { setGuestTimes({}); return }
    let annule = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: 'orchestrateur_guest_times', run_id: ids }),
        })
        if (!res.ok) return
        const json = await res.json().catch(() => ({}))
        if (!annule && json && typeof json === 'object') setGuestTimes(json)
      } catch (_) { /* silencieux : sans réel, on garde le théorique */ }
    })()
    return () => { annule = true }
  }, [isOrchestrateur, runsDeplies])

  // ⚠️ ON ARRIVE PEUT-ÊTRE D'UN AUTRE ÉCRAN. La destination porte le jour à
  // rouvrir ; on la consomme UNE FOIS puis on l'efface, sinon revenir sur
  // l'onglet plus tard rouvrirait le jour d'il y a une heure.
  useEffect(() => {
    const d = pourOnglet(destination, ONGLET_MOIS)
    if (!d) return
    if (d.jour) { setMonth(d.jour.slice(0, 7)); setSelectedDayIso(d.jour); setFiltreAction(null) }
    onDestinationConsommee && onDestinationConsommee()
  }, [destination, onDestinationConsommee])

  return (
    <div style={{ fontFamily: DS.fontBody, color: C.fonce }}>
      <style>{CSS_MOIS}</style>

      {/* ════════ CARTE DE TITRE (v2 2026-08-14) — les CHIFFRES DU MOIS ════════
          Même langage que Suivi équipe : le sous-titre redevient la PHRASE DE
          RÔLE, les comptes descendent dans la rangée de chiffres (passages ·
          à rattraper · agents · de travail). « à rattraper » est LE compteur
          d'alerte de la page (#f5a892 dès qu'il dépasse zéro). Chez l'AGENT,
          pas de compteur « agents » : son mois n'a que lui. */}
      <CarteTitrePage
        titre={titreMois(currentUser?.role)}
        sousTitre={
          <span data-testid="mois-sous-titre">
            {isOrchestrateur
              ? 'Les passages de votre équipe, mois par mois.'
              : 'Vos ménages et interventions, mois par mois.'}
          </span>
        }
        sousLeFilet={(() => {
          // ⚠️ LE CLIC MET AU TRAVAIL, IL NE MONTRE PAS OÙ TRAVAILLER (maquette
          // 2026-08-15) : voile sur la grille + premier jour concerné déplié +
          // « jour suivant » en pied. Au plus DEUX cartes ; aucune → LA LIGNE
          // CALME. Les deux filtres sont exclusifs.
          const nbSans = joursSansAgentTous.length
          const activeSans = filtreAction?.type === 'sans_agent'
          const activeCheck = filtreAction?.type === 'checklists'
          if (nbSans > 0 || checklistsNonValidees > 0) {
            return (
              <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                {nbSans > 0 && (
                  <CarteActionTitre testId="mois-action-sans-agent"
                    fond="rgba(248,220,209,.94)" active={activeSans}
                    icone={<UserRoundSearch size={17} strokeWidth={2.2} color="#8f2f1d" style={{ flexShrink: 0 }} />}
                    titre={nbSans > 1 ? `${nbSans} jours n'ont personne` : "1 jour n'a personne"}
                    titreCouleur="#233240"
                    sousLigne={activeSans
                      ? listeNumerosJours(joursSansAgentTous)
                      : `le plus proche : ${jourNomme(joursSansAgentTous[0])}`}
                    sousLigneCouleur="#8f2f1d" chevronCouleur="#8f2f1d"
                    onClic={() => {
                      setSelectedDayIso(null)
                      setFiltreAction((f) => f?.type === 'sans_agent'
                        ? null
                        : { type: 'sans_agent', jour: joursSansAgentTous[0] })
                    }} />
                )}
                {checklistsNonValidees > 0 && (
                  <CarteActionTitre testId="mois-action-checklists"
                    fond="rgba(255,255,255,.14)" active={activeCheck}
                    icone={<ClipboardX size={17} strokeWidth={2.2} color="#fff" style={{ flexShrink: 0 }} />}
                    titre={`${checklistsNonValidees} checklist${checklistsNonValidees > 1 ? 's' : ''} pas validée${checklistsNonValidees > 1 ? 's' : ''}`}
                    titreCouleur="#fff"
                    sousLigne={activeCheck
                      ? listeNumerosJours(mois.joursEnRetard)
                      : `la plus ancienne : ${jourEtMois(mois.joursEnRetard[0])}`}
                    sousLigneCouleur="rgba(255,255,255,.72)" chevronCouleur="rgba(255,255,255,.6)"
                    onClic={() => {
                      setSelectedDayIso(null)
                      setFiltreAction((f) => f?.type === 'checklists'
                        ? null
                        : { type: 'checklists', jour: mois.joursEnRetard[0] })
                    }} />
                )}
              </div>
            )
          }
          const estRevolu = month < today.slice(0, 7)
          return (
            <div data-testid="mois-ligne-calme"
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: '50%',
                flexShrink: 0, background: 'rgba(219,233,226,.9)', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center' }}>
                <Check size={16} strokeWidth={2.6} color="#1f5a43" />
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: '#fff' }}>
                  {estRevolu ? `${nomDuMois(month)} est clos` : 'Le mois est en ordre'}
                </span>
                <span style={{ display: 'block', fontSize: 10.5, marginTop: 1, color: 'rgba(255,255,255,.72)' }}>
                  {estRevolu
                    ? (mois.total > 0
                      ? `${mois.total} passage${mois.total > 1 ? 's' : ''}, tous validés`
                      : 'aucun passage ce mois-là')
                    : (mois.total > 0
                      // ⚠️ VRAI Y COMPRIS AUJOURD'HUI (2026-08-22) : « rien en
                      // retard » n'affirme pas que les ménages du jour, encore
                      // en cours, seraient déjà validés.
                      ? 'rien en retard, tous les jours ont un agent'
                      : 'aucun ménage programmé pour l\'instant')}
                </span>
              </span>
              {/* Un mois RÉVOLU n'a pas d'« à venir » — il n'y a plus rien qui vienne. */}
              {!estRevolu && mois.aVenir > 0 && (
                <span data-testid="mois-calme-a-venir"
                  style={{ fontSize: 11, color: 'rgba(255,255,255,.72)', flexShrink: 0 }}>
                  {mois.aVenir} passage{mois.aVenir > 1 ? 's' : ''} à venir
                </span>
              )}
            </div>
          )
        })()}
        actions={
          /* Le sélecteur vit DANS la carte, à droite du titre (maquette
             2026-08-15) : chevrons 34 px translucides, mois 13 px blanc.
             Le clic sur le mois ramène au mois courant (comportement gardé). */
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <NavMois label="Mois précédent" onClick={() => setMonth(shiftMonth(month, -1))}>
              <ChevronLeft size={16} strokeWidth={2.2} />
            </NavMois>
            <button type="button" data-testid="mois-libelle"
              onClick={() => setMonth(today.slice(0, 7))}
              disabled={month === today.slice(0, 7)}
              title={month === today.slice(0, 7) ? undefined : 'Revenir au mois courant'}
              style={{ minWidth: 74, textAlign: 'center', background: 'transparent', border: 'none',
                color: '#fff', fontFamily: DS.fontBody, fontSize: 12.5, fontWeight: 600,
                textTransform: 'capitalize', padding: '5px 4px',
                cursor: month === today.slice(0, 7) ? 'default' : 'pointer' }}>
              {monthLabelFR(month)}
            </button>
            <NavMois label="Mois suivant" onClick={() => setMonth(shiftMonth(month, +1))}>
              <ChevronRight size={16} strokeWidth={2.2} />
            </NavMois>
          </div>
        } />
      {basculePage}

      {loading && <div style={{ padding: 40, textAlign: 'center', color: C.muet, fontSize: 13 }}>Chargement…</div>}
      {error && (
        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', margin: '14px 20px',
          padding: 14, background: DS.dangerBg, color: DS.dangerText, borderRadius: 10, fontSize: 13 }}>
          <AlertTriangle size={16} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} /> {error}
        </div>
      )}

      {!loading && !error && (
        <div className="fbz-mois">
          <main className="fbz-mois-main">
            <div className="fbz-mois-colonne">
            <GrilleMois
              year={gridYear} month1={gridMonth1} todayIso={today}
              renderDaySlot={renderDaySlot}
              selectedDayIso={selectedDayIso} onDayClick={handleDayClick}
              joursEclaires={joursConcernes ? new Set(joursConcernes) : null} />

            {/* ⚠️ LA LÉGENDE LISTE TOUT CE QUI PORTE UNE COULEUR DE CASE, et
                rien d'autre. L'ancienne n'expliquait QUE la pastille corail : le
                vert et le rouge, qui portent justement une décision, n'y étaient
                pas. À l'inverse, le validé et le « à venir » n'ont AUCUNE couleur
                de case — leur pastille se comprend seule, une coche ou un
                chiffre ; les mettre en légende ferait décoder ce qui n'est pas
                codé. */}
            {/* ⚠️ LA LÉGENDE PORTE AUSSI LES ICÔNES (Dimitri 2026-08-14) : la
                coche « tout validé », la silhouette « personne d'assigné » et
                l'horloge des demandes manquaient — trois formes à décoder sans
                aide. « Aujourd'hui » = carré à BORDURE, comme la case. */}
            <div data-testid="mois-legende"
              style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12,
                alignItems: 'center' }}>
              <Entree fond={C.alerteFond} bord={C.alerteBord} texte="Passé non validé" />
              <Entree fond={C.alerteFond} texte="Personne d'assigné"
                icone={<UserRoundSearch size={8} strokeWidth={2.6} color={C.alerteTexte} />} />
              <Entree fond="transparent" bord={C.gris} epais texte="Aujourd'hui" />
              <Entree fond={C.pastValide} rond texte="Tout validé"
                icone={<Check size={8} strokeWidth={3} color={C.pastValideTexte} />} />
              <Entree fond={C.demandeFond} rond texte="Demande d'horaire"
                icone={<Clock size={9} strokeWidth={2.4} color="#fff" />} />
              {/* GO 2026-08-19 — les contrôles sur la grille. */}
              <Entree fond="#b8412b" rond texte="un contrôle à reprendre"
                icone={<Eye size={9} strokeWidth={2.6} color="#fff" />} />
              <Entree fond="#f7fbf9" bord="#c9e0d4" texte="journée entièrement validée" />
            </div>

            {/* ⚠️ LA RÈGLE, SOUS LA LÉGENDE. La légende nomme chaque couleur une
                par une ; la règle dit ce que signifie leur ABSENCE — et l'absence
                ne s'infère pas d'une liste de présences. */}
            <div data-testid="mois-regle"
              style={{ marginTop: 10, background: C.colonne, borderRadius: 10,
                padding: '10px 13px', fontSize: 11.5, color: C.fonce, lineHeight: 1.5 }}>
              Une case colorée demande quelque chose. Le reste s'est bien passé.
            </div>

            {/* ════════ DÉPLIAGE ════════ */}
            </div>
            <div className="fbz-mois-colonne">
            {joursDeplies.map((iso) => (
              <MoisJourDetail key={iso} dayIso={iso}
                controlesParRun={controlesParRun}
                controleursDefaut={controleursDefaut}
                onControler={onControler}
                onOuvrirControle={(c) => setDetailControle(c)}
                runs={runsAffiches.filter((r) => r.scheduled_date === iso && r.status !== 'skipped')}
                interventions={interventionsAffichees.filter((it) => it.scheduled_date === iso)}
                demandes={horaires.filter((d) => d.event_date === iso)}
                onFermer={() => { setFiltreAction(null); setSelectedDayIso((c) => (c === iso ? null : c)) }}
                infoDe={infoDe} logementNom={nomDe}
                currentUser={currentUser} assignOptions={assignOptions} regles={regles}
                guestTimes={guestTimes}
                onReassign={handleReassign}
                onOpenChecklist={(run) => setChecklistModal({ run })}
                onSignalIncident={(run) => setIncidentModal({ run })}
                onOuvrirDuree={(bloc) => setModaleDuree({ bloc })}
                onAllerFrise={(jour) => onAller && onAller(versLaFrise(jour, { onglet: ONGLET_MOIS, jour }))}
                onAllerDemande={(d, jour) => onAller && onAller(versLaDemande(d.id, jour, { onglet: ONGLET_MOIS, jour }))}
                // ⚠️ MODE FILTRE (carte d'action active) : chaque ligne porte son
                // bouton (« Assigner » / « Remplir ») et le pied navigue de jour
                // concerné en jour concerné sans repasser par la grille.
                boutonsAction={!!filtreAction?.jour}
                piedSuivant={(() => {
                  if (!filtreAction?.jour || !joursConcernes) return null
                  const idx = joursConcernes.indexOf(iso)
                  const suivant = idx >= 0
                    ? joursConcernes[idx + 1]
                    // case hors filtre ouverte entre-temps : on reprend au
                    // premier jour concerné APRÈS ce jour-là.
                    : joursConcernes.find((j) => j > iso)
                  return {
                    dernier: !suivant,
                    onSuivant: suivant
                      ? () => { setSelectedDayIso(null); setFiltreAction((f) => ({ ...f, jour: suivant })) }
                      : undefined,
                  }
                })()} />
            ))}
            </div>
          </main>

          {/* ════════ COLONNE DE CONTEXTE ════════ */}
          <aside className="fbz-mois-aside">
            {/* ⚠️ LA CARTE ROSÉE « N sans agent » EST RETIRÉE (maquette
                2026-08-15) : doublon de la carte « N jours n'ont personne » du
                bandeau — qui fait mieux (voile + jour ouvert). */}
            <Bloc>
              <div data-testid="mois-carte-total"
                style={{ background: C.gris, borderRadius: 12, padding: '12px 14px', color: '#fff' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: '#a8bfd4',
                  textTransform: 'uppercase', letterSpacing: '.08em' }}>Ce mois</div>
                {/* « passages » : le total mêle ménages ET interventions — et il
                    reste INERTE : c'est une somme, elle n'appelle rien. */}
                <div style={{ fontFamily: DS.fontDisplay, fontSize: 20, fontWeight: 600,
                  lineHeight: 1, marginTop: 4 }}>
                  {mois.total} passage{mois.total > 1 ? 's' : ''}
                </div>
                {/* Les deux comptes ÉCLAIRENT la grille — même voile, MÊME état
                    de filtre que les cartes du bandeau (un seul actif). */}
                <button type="button" data-testid="mois-filtre-valides"
                  className="fbz-press fbz-cible-tactile" aria-pressed={filtreAction?.type === 'valides'}
                  onClick={() => { setSelectedDayIso(null); setFiltreAction((f) => f?.type === 'valides' ? null : { type: 'valides' }) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                    background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                    borderTop: '1px solid rgba(255,255,255,.13)', marginTop: 9,
                    padding: '7px 0', fontFamily: DS.fontBody }}>
                  <Check size={14} strokeWidth={2.4} color="#8fd6b4" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 11.5, color: '#fff' }}>
                    {mois.valides} validé{mois.valides > 1 ? 's' : ''}
                  </span>
                </button>
                <button type="button" data-testid="mois-filtre-a-venir"
                  className="fbz-press fbz-cible-tactile" aria-pressed={filtreAction?.type === 'a_venir'}
                  onClick={() => { setSelectedDayIso(null); setFiltreAction((f) => f?.type === 'a_venir' ? null : { type: 'a_venir' }) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                    background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                    borderTop: '1px solid rgba(255,255,255,.13)',
                    padding: '7px 0', fontFamily: DS.fontBody }}>
                  <CalendarDays size={14} strokeWidth={2.2} color="#87b8e6" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 11.5, color: '#fff' }}>
                    {mois.aVenir} à venir
                  </span>
                  <ChevronRight size={14} strokeWidth={2.2} color="rgba(255,255,255,.55)" style={{ flexShrink: 0 }} />
                </button>
              </div>
            </Bloc>

            {/* ⚠️ LES HEURES DU MOIS N'EXISTAIENT NULLE PART. Personne ne voyait
                qu'un agent en fait 51 et un autre 30. Étendu 2026-08-14 : le
                même compte PAR LOGEMENT. Recadrage 2026-08-22 : AUCUN filtre —
                les deux modes montrent tout le mois ; « Par agent » se déplie
                (le détail ligne par ligne), « Par logement » compte seulement. */}
            {(charges.length > 0 || chargesLogement.length > 0) && (
              <Bloc titre="Récapitulatif">
                {/* RÈGLE DU DESSIN UNIQUE (Dimitri 2026-08-19) : un même
                    geste, une même forme — la bascule de la nav, partout. */}
                <div style={{ marginBottom: 8 }}>
                  <BasculeEspace testId="mois-recap" actif={modeRecap} onChange={setModeRecap}
                    entrees={[
                      { id: 'agent', label: 'Par agent', icone: UserIconBascule },
                      { id: 'logement', label: 'Par logement', icone: Home },
                    ]} />
                </div>
                {/* ⚠️ LE RÉCAP DIT CE QU'IL COMPTE (Dimitri 2026-08-22) : les
                    checklists VALIDÉES seulement — heures comprises. La charge
                    à venir vit sur sa propre ligne discrète, jamais mélangée. */}
                <div data-testid="mois-recap-intertitre"
                  style={{ margin: '0 2px 7px', fontSize: 9.5, fontWeight: 700,
                    letterSpacing: '.08em', textTransform: 'uppercase', color: C.muet }}>
                  Ménages terminés
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(modeRecap === 'agent' ? charges : chargesLogement).map((c) => {
                    const valeur = modeRecap === 'agent' ? c.email : c.logementId
                    // LES DEUX MODES SE DÉPLIENT (correction Dimitri 2026-08-22) :
                    // même mécanique, seule change la colonne qui varie — le
                    // logement dans « Par agent », l'AGENT dans « Par logement ».
                    // AUCUN filtre, ni ici ni là.
                    const depliable = true
                    const ouvert = chargeDepliee === valeur
                    return (
                      <div key={valeur}>
                      <button type="button"
                        data-testid={`mois-charge-${valeur}`}
                        aria-expanded={depliable ? ouvert : undefined}
                        onClick={() => setChargeDepliee((a) => (a === valeur ? null : valeur))}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                          textAlign: 'left', cursor: depliable ? 'pointer' : 'default',
                          fontFamily: DS.fontBody, background: '#fff',
                          border: `1px solid ${C.bord}`,
                          borderRadius: 9, padding: '8px 10px' }}>
                        {modeRecap === 'agent' ? (
                          // Pastille CIEL PLEIN texte blanc — le même langage
                          // que les cartes du jour (maquette 2026-08-19).
                          // ⚠️ JAMAIS SES PROPRES INITIALES (règle 2026-08-23) :
                          // sa ligne porte « MOI ». Celles des autres restent.
                          <span style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: c.email === currentUser?.email ? 8 : 9, fontWeight: 700,
                            background: '#5c8ab8', color: '#fff' }}>
                            {c.email === currentUser?.email
                              ? 'MOI'
                              : String(initialsFromEmail(c.email) || '??').toUpperCase().slice(0, 2)}
                          </span>
                        ) : (
                          <span style={{ width: 24, height: 24, flexShrink: 0, display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center' }}>
                            <Home size={16} strokeWidth={2} color={C.gris} />
                          </span>
                        )}
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: C.gris,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {modeRecap === 'agent'
                              ? (c.email === currentUser?.email ? 'Moi' : libelleDepuisEmail(c.email))
                              : nomDe(c.logementId)}
                          </span>
                          {/* ⚠️ `nowrap` + ellipsis : « 57 h 30 » cassé en deux
                              lignes se lit comme deux nombres. 11px (et plus
                              9,5) sur demande Dimitri 2026-08-14 — cible de
                              lisibilité avant la peur de l'ellipse. Les heures
                              n'existent qu'en mode agent : « Par logement » =
                              le nombre seul (Dimitri 2026-08-22). */}
                          {modeRecap === 'agent' && (
                            <span style={{ display: 'block', fontSize: 11, color: C.muet,
                              fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                              overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {c.minutes ? formaterDuree(c.minutes) : 'rien encore'}
                            </span>
                          )}
                          {/* La charge À VENIR — discrète, à part du fait. */}
                          {c.aVenir > 0 && (
                            <span data-testid={`mois-charge-avenir-${valeur}`}
                              style={{ display: 'block', fontSize: 10, color: C.muet,
                                whiteSpace: 'nowrap', marginTop: 1 }}>
                              + {c.aVenir} prévu{c.aVenir > 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                        {/* Le COMPTE en pastille ciel — le même langage que la
                            durée des cartes du jour ; le chiffre suffit. */}
                        <span data-testid={`mois-charge-compte-${valeur}`}
                          style={{ flexShrink: 0, minWidth: 22, textAlign: 'center',
                            fontSize: 10.5, fontWeight: 700, color: '#3d5166',
                            background: '#e8f2fa', borderRadius: 8, padding: '3px 7px',
                            fontVariantNumeric: 'tabular-nums' }}>
                          {c.nb}
                        </span>
                        {depliable && (ouvert
                          ? <ChevronDown size={14} strokeWidth={2.2} color="#c3ccd4" style={{ flexShrink: 0 }} />
                          : <ChevronRight size={14} strokeWidth={2.2} color="#c3ccd4" style={{ flexShrink: 0 }} />)}
                      </button>
                      {ouvert && <DetailCharge mode={modeRecap} valeur={valeur} runs={runs}
                        interventions={interventions} nomDe={nomDe} />}
                      </div>
                    )
                  })}
                </div>
              </Bloc>
            )}

          </aside>
        </div>
      )}

      {message && (
        <div data-testid="mois-message"
          style={{ position: 'fixed', left: 16, bottom: 16, zIndex: 70, display: 'inline-flex',
            gap: 7, alignItems: 'center', padding: '9px 13px', borderRadius: 9, fontSize: 12,
            background: message.startsWith('✅') ? DS.successBg : DS.dangerBg,
            color: message.startsWith('✅') ? DS.successText : DS.dangerText }}>
          {message.replace(/^[✅❌]\s*/, '')}
        </div>
      )}

      {modaleDuree && (
        <ModaleDuree
          bloc={modaleDuree.bloc}
          logement={{ menage_duree_min: infoDe(modaleDuree.bloc.logementId)?.dureeMenage ?? null }}
          onFermer={() => setModaleDuree(null)}
          onValider={async ({ minutes, aussiParDefaut }) => {
            // L'exception de CE ménage d'abord ; puis, si la case est cochée,
            // le défaut du LOGEMENT — même chemin serveur que la frise.
            // (Avant 2026-08-19 la case s'affichait ici mais était IGNORÉE en
            // silence : cochée, rien ne se passait — mensonge d'écran corrigé.)
            const { ok, erreur } = await fixerDureeRun(supabase, modaleDuree.bloc.id, minutes)
            if (!ok) { setMessage(`❌ ${erreur}`); setModaleDuree(null); return }
            if (aussiParDefaut && modaleDuree.bloc.logementId) {
              const d = await fixerDureeDefautLogement(modaleDuree.bloc.logementId, minutes)
              setMessage(d.ok
                ? '✅ Durée du ménage posée, et défaut du logement mis à jour.'
                : `❌ Durée posée pour ce ménage, mais le défaut du logement n'a pas pu être enregistré. ${d.erreur}`)
            } else {
              setMessage('✅ Durée enregistrée pour ce ménage.')
            }
            await reload()
            setModaleDuree(null)
          }} />
      )}

      {/* Le détail LECTURE SEULE d'un contrôle — ouvert par le rond de
          contrôle d'une ligne du jour (même panneau que la frise). */}
      {detailControle && (
        <PanneauDetailControle
          controleId={detailControle.id}
          onFermer={() => setDetailControle(null)}
        />
      )}

      {checklistModal && (
        <ChecklistRunModal run={checklistModal.run} currentUser={currentUser} cfg={cfg}
          onClose={() => setChecklistModal(null)}
          onCompleted={async () => { setChecklistModal(null); await reload() }} />
      )}

      {incidentModal && (
        <IncidentModal
          incident={null} cfg={cfg} data={null} isAdmin={false}
          userLogIds={[incidentModal.run.logement_id]}
          defaultLogId={incidentModal.run.logement_id}
          run={incidentModal.run}
          onClose={() => setIncidentModal(null)}
          onSave={async (payload) => {
            await createIncident({
              logement_id: payload.logId, run_id: incidentModal.run?.id || null,
              source: 'run', reported_by: currentUser?.email,
              voyageur: payload.voyageur || null,
              casse_interne: !!payload.casse_interne, incident_date: payload.date || null,
              degradations: payload.degradations || [], photos: payload.photos || [],
              label: (payload.degradations?.[0]?.type) || 'Incident multi-dégradations',
              description: (payload.degradations || []).map(d => `${d.type} (${d.nombre || 1}) ${d.precision || ''}`.trim()).join(' · '),
              status: STATUS.EN_COURS,
            })
            setIncidentModal(null)
            await reload()
          }} />
      )}
    </div>
  )
}


/**
 * LA GRILLE DU MOIS — locale.
 *
 * L'ancien cœur partagé `SharedMonthCalendar` (retiré le 03/10 : plus aucun
 * écran ne l'utilisait, étiquette git archive/code-mort-p2) avait une cellule
 * incompatible avec la maquette : numéro de jour EN HAUT À DROITE, « auj. »
 * collé, fonds peints par lui, et un seul emplacement SOUS tout ça. La maquette
 * demande l'inverse — un numéro en haut à gauche, un fond piloté par l'état, et
 * rien d'autre dans la case.
 *
 * On réutilise donc ce qui compte vraiment : `buildMonthGrid`, la géométrie
 * (semaines complètes, jours hors mois), déjà testée et partagée. Seule la
 * PEINTURE est locale — c'est elle, et elle seule, qui diverge.
 */
function GrilleMois({ year, month1, todayIso, renderDaySlot, selectedDayIso, onDayClick, estCliquable, joursEclaires }) {
  const semaines = useMemo(() => buildMonthGrid(year, month1, true), [year, month1])
  return (
    <div data-testid="mois-grille">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5,
        marginBottom: 5 }}>
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((j, i) => (
          <span key={i} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700,
            color: C.muet, fontFamily: DS.fontBody }}>{j}</span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 5 }}>
        {semaines.flat().map((c) => (
          <div key={c.iso} data-testid={`case-${c.iso}`}
            // ⚠️ TOUTE CASE DU MOIS SE DÉPLIE (Dimitri 2026-08-22, inverse la
            // décision du 14/08) : un jour vide s'ouvre, dit « Rien de prévu
            // ce jour-là » et offre le chemin de la frise pour créer une
            // intervention.
            onClick={c.inMonth && (!estCliquable || estCliquable(c.iso)) ? () => onDayClick(c.iso) : undefined}
            style={{ aspectRatio: '1', minWidth: 0,
              cursor: c.inMonth && (!estCliquable || estCliquable(c.iso)) ? 'pointer' : 'default',
              borderRadius: 7,
              // ⚠️ LE VOILE (carte d'action active, 2026-08-15) : tout s'estompe
              // SAUF les jours concernés — un simple voile visuel, AUCUNE donnée
              // ne change. La transition douce fait apparaître le MOTIF (les
              // trous qui tombent tous le week-end, par exemple).
              opacity: joursEclaires && !joursEclaires.has(c.iso) ? 0.2 : 1,
              transition: 'opacity .22s',
              // Le jour sélectionné se signale par un anneau ciel — pas par un
              // fond, qui écraserait la couleur d'état de la case.
              boxShadow: selectedDayIso === c.iso ? `0 0 0 2px ${C.ciel}` : undefined }}>
            {c.inMonth ? renderDaySlot(c.iso) : <HorsMois iso={c.iso} />}
          </div>
        ))}
      </div>
    </div>
  )
}

/** ⚠️ HORS MOIS : le numéro, et rien d'autre. Pas de contour, pas de fond — la
 *  case existe pour tenir la grille droite, pas pour être lue. */
function HorsMois({ iso }) {
  return (
    <div style={{ width: '100%', height: '100%', padding: 4, boxSizing: 'border-box' }}>
      <span style={{ fontSize: 9.5, fontWeight: 700, color: C.horsMois,
        fontFamily: DS.fontBody, lineHeight: 1 }}>
        {Number(String(iso).slice(-2))}
      </span>
    </div>
  )
}

/** « 11 au 17 août » — deux jours d'un même mois ne répètent pas le mois. */
// ── LE DÉTAIL D'UNE LIGNE DU RÉCAP (Dimitri 2026-08-22) — « pas seulement
// un total : je veux pouvoir vérifier ligne par ligne. » Les ménages VALIDÉS
// du mois (jour · heure · colonne variable), puis « Ce qui arrive » : les
// à venir, même forme. La colonne variable est le LOGEMENT en mode agent, et
// l'AGENT en mode logement — l'autre dimension est déjà connue par la ligne.
// Les interventions comptent dans la charge : elles se listent aussi, nommées.
function DetailCharge({ mode, valeur, runs, interventions, nomDe }) {
  const lignes = useMemo(() => {
    const faits = []
    const prevus = []
    const garde = mode === 'agent'
      ? (x) => x?.assigned_provider_email === valeur
      : (x) => x?.logement_id === valeur
    const colonneRun = (r) => (mode === 'agent'
      ? nomDe(r.logement_id)
      : (r.assigned_provider_email ? libelleDepuisEmail(r.assigned_provider_email) : 'sans agent'))
    const colonneInter = (it) => {
      const libelle = `Intervention — ${it.libelle || 'sans libellé'}`
      if (mode === 'agent') return libelle
      const qui = it.assigned_provider_email ? libelleDepuisEmail(it.assigned_provider_email) : 'sans agent'
      return `${qui} · ${libelle}`
    }
    for (const r of runs) {
      if (!garde(r) || r?.status === 'skipped') continue
      const cible = r.status === 'completed' ? faits : prevus
      cible.push({
        id: r.id, date: r.scheduled_date, nom: colonneRun(r),
        heure: r.status === 'completed'
          ? heureValideeFR(r.completed_at)
          : heureFixeeFR(r.heure_fixee),
      })
    }
    for (const it of interventions) {
      if (!garde(it) || it?.statut === 'skipped') continue
      const cible = it.statut === 'completed' ? faits : prevus
      cible.push({
        id: it.id, date: it.scheduled_date, nom: colonneInter(it),
        heure: it.statut === 'completed' ? heureValideeFR(it.terminee_le) : heureFixeeFR(it.heure_fixee),
      })
    }
    const tri = (a, b) => String(a.date).localeCompare(String(b.date)) || String(a.id).localeCompare(String(b.id))
    return { faits: faits.sort(tri), prevus: prevus.sort(tri) }
  }, [mode, valeur, runs, interventions, nomDe])

  // ⚠️ AUCUNE LARGEUR FIGÉE (débordement Dimitri 2026-08-22) : les anciennes
  // colonnes à minWidth (52 + 38 px) ne savaient pas rétrécir — dans une
  // colonne étroite, elles POUSSAIENT le contenu hors du cadre. La grille
  // partagée aligne jour et heure sur leur contenu réel, et le nom prend le
  // reste : il SE COUPE proprement, il ne pousse jamais.
  const Ligne = ({ l }) => (
    <>
      <span style={{ fontVariantNumeric: 'tabular-nums', color: C.gris, fontWeight: 700, whiteSpace: 'nowrap' }}>
        {jourEtMois(l.date)}
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {l.heure || '—'}
      </span>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={l.nom}>
        {l.nom}
      </span>
    </>
  )
  const pleineLargeur = { gridColumn: '1 / -1' }

  return (
    <div data-testid={`mois-detail-agent-${valeur}`}
      style={{ margin: '4px 0 2px', padding: '8px 11px', background: C.colonne,
        border: `1px solid ${C.bord}`, borderRadius: 9, overflow: 'hidden',
        display: 'grid', gridTemplateColumns: 'max-content max-content minmax(0, 1fr)',
        columnGap: 7, rowGap: 5, alignItems: 'baseline',
        fontSize: 11, color: C.texteDoux, fontFamily: DS.fontBody }}>
      {lignes.faits.length === 0 && (
        <div style={{ ...pleineLargeur, fontSize: 11, color: C.muet }}>Rien de validé ce mois-ci.</div>
      )}
      {lignes.faits.map((l) => <Ligne key={`f-${l.id}`} l={l} />)}
      {lignes.prevus.length > 0 && (
        <>
          <div style={{ ...pleineLargeur, margin: '4px 0 0', fontSize: 9, fontWeight: 700,
            letterSpacing: '.08em', textTransform: 'uppercase', color: C.muet }}>
            Ce qui arrive
          </div>
          {lignes.prevus.map((l) => <Ligne key={`p-${l.id}`} l={l} />)}
        </>
      )}
    </div>
  )
}

/** ISO datetime → « 11h40 » Paris — l'heure du validé. */
function heureValideeFR(iso) {
  if (!iso) return null
  try {
    const hm = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso)).replace(':', 'h')
    const net = hm.startsWith('0') ? hm.slice(1) : hm
    return net.endsWith('h00') ? net.slice(0, -2) : net
  } catch (_) { return null }
}

/** « 14:30:00 » → « 14h30 » ; « 14:00:00 » → « 14h » ; absent → null. */
function heureFixeeFR(hf) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hf || ''))
  if (!m) return null
  return m[2] === '00' ? `${Number(m[1])}h` : `${Number(m[1])}h${m[2]}`
}

function NavMois({ label, onClick, children }) {
  return (
    // 32 px au dessin (maquette carte 2026-08-15) — la classe .fbz-cible-tactile
    // garde les 44 px de zone de contact en mobile (leçon du 30 px au doigt).
    <button type="button" onClick={onClick} aria-label={label} title={label}
      className="fbz-press fbz-cible-tactile"
      style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', borderRadius: 9, border: 'none',
        background: 'rgba(255,255,255,.14)', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
      {children}
    </button>
  )
}

// 11px (et plus 9.5) : c'était le plus petit corps de l'espace (constat 2026-08-14).
function Entree({ fond, bord, epais = false, rond = false, icone = null, texte }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span aria-hidden="true" style={{ width: rond ? 14 : 12, height: rond ? 14 : 12,
        borderRadius: rond ? '50%' : 3, background: fond,
        boxShadow: bord ? `inset 0 0 0 ${epais ? 2 : 1.5}px ${bord}` : undefined,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icone}
      </span>
      <span style={{ fontSize: 11, color: C.texteDoux }}>{texte}</span>
    </span>
  )
}

function Bloc({ titre, children, refExterne }) {
  return (
    <section ref={refExterne} style={{ marginBottom: 16 }}>
      {titre && (
        <h2 style={{ fontFamily: DS.fontBody, fontSize: 10.5, fontWeight: 700, color: C.muet,
          textTransform: 'uppercase', letterSpacing: '.09em', margin: '0 0 8px' }}>{titre}</h2>
      )}
      {children}
    </section>
  )
}
